// mj-trust: loaded only where the section is used (sections/mj-trust.liquid)

// Mobile marquee: a seamless, continuous loop at any phone width.
// - Measures the real width of one set of items (no reliance on how a browser sizes the track).
// - Repeats the set until the track is at least twice the screen width; the copies are hidden from
//   screen readers and keyboard (aria-hidden, inert).
// - Moves the track by exactly one set (--mj-marquee-distance), so the loop point is invisible.
// - Pauses while touched. Reduced motion: no animation (the CSS shows a swipeable row instead).
const mobile = window.matchMedia('(max-width: 749px)');

class MjTrustMarquee extends HTMLElement {
  #observer = null;
  #width = 0;

  connectedCallback() {
    const pause = () => this.setAttribute('data-paused', '');
    const resume = () => this.removeAttribute('data-paused');
    this.addEventListener('touchstart', pause, { passive: true });
    this.addEventListener('touchend', resume, { passive: true });
    this.addEventListener('touchcancel', resume, { passive: true });

    if (this.closest('.mj-trust--mobile-grid')) return;

    this.#observer = new ResizeObserver(() => this.#layout());
    this.#observer.observe(this);
    mobile.addEventListener('change', this.#onBreakpoint);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    mobile.removeEventListener('change', this.#onBreakpoint);
  }

  #onBreakpoint = () => {
    this.#width = 0;
    this.#layout();
  };

  #layout() {
    const track = this.querySelector('.mj-trust__track');
    const original = track?.querySelector('.mj-trust__list:not(.mj-trust__list--clone)');
    if (!track || !original || !mobile.matches) return;

    const viewport = Math.round(this.getBoundingClientRect().width);
    if (!viewport || viewport === this.#width) return;
    this.#width = viewport;

    const setWidth = original.getBoundingClientRect().width;
    if (!setWidth) return;

    // Enough sets for the track to be at least twice the screen width (and never fewer than two).
    const needed = Math.max(2, Math.ceil((2 * viewport) / setWidth) + 1);
    let sets = [...track.querySelectorAll('.mj-trust__list')];
    while (sets.length < needed) {
      track.append(this.#copy(original));
      sets = [...track.querySelectorAll('.mj-trust__list')];
    }
    sets.slice(needed).forEach((set) => set.remove());

    // Move by exactly one set, then restart the animation so it uses the new distance.
    track.style.setProperty('--mj-marquee-distance', `${-setWidth}px`);
    track.style.animation = 'none';
    void track.offsetWidth;
    track.style.animation = '';
  }

  /** A copy of the set for the loop: hidden from screen readers, not focusable, not editable. */
  #copy(original) {
    const copy = /** @type {HTMLElement} */ (original.cloneNode(true));
    copy.classList.add('mj-trust__list--clone');
    copy.setAttribute('aria-hidden', 'true');
    copy.setAttribute('inert', '');
    copy.querySelectorAll('[data-shopify-editor-block]').forEach((item) => item.removeAttribute('data-shopify-editor-block'));
    copy.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    return copy;
  }
}

if (!customElements.get('mj-trust-marquee')) customElements.define('mj-trust-marquee', MjTrustMarquee);
